// src/employees/EmployeeScheduling.js
import React, { useState, useEffect } from 'react';
import { format, addDays, startOfWeek, isSameDay, startOfMonth, addMonths, startOfYear, addYears, isSameMonth } from 'date-fns';
import axios from 'axios';
import { API_BASE } from '../apiBase';
import { Maximize2, Minimize2, Send } from 'lucide-react';
import { EVENT_TYPES, EventBadge } from '../lib/eventTypes';
import { getAddressSuggestions } from '../lib/googlePlaces';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Frames the map around every visible dot: one dot = center on it, several =
// fit them all with padding. No dots = leave the default view alone.
function MapClickClear({ onClear }) {
  const map = useMap();
  useEffect(() => {
    map.on('click', onClear); // marker clicks don't bubble here — only bare map
    return () => map.off('click', onClear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
}

function FitToPins({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 13);
    else map.fitBounds(L.latLngBounds(points), { padding: [50, 50], maxZoom: 14 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points)]);
  return null;
}

function AssignmentBlock({ assignment, color, onClick, onHoverChange, isDuplicateMode, notifHref, po }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: assignment.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.85 : 1,
    transition: 'opacity 0.18s ease',
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, backgroundColor: isDuplicateMode ? '#064e3b50' : `${color}30`, borderLeft: `4px solid ${color}` }}
      className="rounded-lg p-2.5 text-sm shadow-md cursor-grab active:cursor-grabbing select-none hover:shadow-md transition-shadow z-10 relative"
      onClick={onClick}
      onMouseEnter={() => onHoverChange?.(assignment.id)}
      onMouseLeave={() => onHoverChange?.(null)}
      {...listeners}
      {...attributes}
    >
      {isDuplicateMode && (
        <span className="absolute top-1 right-1 text-emerald-400 text-xs font-bold">+</span>
      )}
      <div className="flex items-start justify-between gap-1.5">
        <div className="font-medium truncate">{assignment.project}</div>
        <span className="text-[8px] font-bold text-zinc-400/80 shrink-0 mt-0.5">PO: {po || 'N/A'}</span>
      </div>
      <div className="text-xs text-zinc-300 truncate">{assignment.notes}</div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <EventBadge type={assignment.eventType} />
        {notifHref && (
          <a
            href={notifHref}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            title="Notify Employee"
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/40 hover:bg-black/70 text-zinc-300 hover:text-white transition-colors"
          >
            <Send size={10} />
          </a>
        )}
      </div>
    </div>
  );
}

function DroppableDay({ id, isSelected = false, isToday = false, onContextMenu, children }) {
  const { setNodeRef } = useDroppable({ id });

  // Purple wash = you're looking at TODAY; white wash = another day.
  const wash = isSelected ? (isToday ? 'bg-indigo-500/10' : 'bg-white/[0.06]') : '';
  return (
    <div
      ref={setNodeRef}
      onContextMenu={onContextMenu}
      className={`p-3 border-b border-gray-800 min-h-[100px] relative flex-1 min-w-[160px] ${wash}`}
    >
      {children}
    </div>
  );
}

function EmployeeScheduling() {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  // Calendar scale: week (default grid) → month (mini assignment chips) →
  // year (12 mini-months with dots) → back to week. The "Week of" button cycles.
  const [calView, setCalView] = useState('week');
  // The day the map points at while in month/year view (defaults to today).
  const [altSelectedDate, setAltSelectedDate] = useState(new Date());
  const cycleCalView = () => {
    setAltSelectedDate(new Date());
    setCalView(v => v === 'week' ? 'month' : v === 'month' ? 'year' : 'week');
  };

  // REAL employees from the database (Active only), colored from a palette.
  const EMP_COLORS = ['#a3e635', '#60a5fa', '#f472b6', '#fbbf24', '#34d399', '#c084fc', '#f87171', '#22d3ee'];
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/employees`, { headers: { Authorization: token } })
      .then(r => setEmployees((r.data || [])
        .filter(e => (e.status || 'Active') === 'Active')
        .map((e, i) => ({
          id: e._id, name: e.name, role: e.role || '', color: EMP_COLORS[i % EMP_COLORS.length], phone: e.phone || '',
          homeAddr: [e.homeAddress?.street, e.homeAddress?.city, e.homeAddress?.state, e.homeAddress?.zip].filter(Boolean).join(', '),
        }))))
      .catch(() => setEmployees([]));
  }, []);

  // REAL assignments for the visible range (week / month / year).
  const [assignments, setAssignments] = useState([]);
  const fetchAssignments = () => {
    const token = localStorage.getItem('token');
    let fromD, toD;
    if (calView === 'month') { fromD = startOfMonth(currentWeekStart); toD = addMonths(fromD, 1); }
    else if (calView === 'year') { fromD = startOfYear(currentWeekStart); toD = addYears(fromD, 1); }
    else { fromD = currentWeekStart; toD = addDays(currentWeekStart, 7); }
    const from = fromD.toISOString();
    const to = toD.toISOString();
    axios.get(`${API_BASE}/schedule-assignments?from=${from}&to=${to}`, { headers: { Authorization: token } })
      .then(r => setAssignments((r.data || []).map(a => ({
        id: a._id, employeeId: a.employee, date: a.date, project: a.projectLabel, startTime: a.startTime || '',
        endTime: a.endTime || '', customerName: a.customerName || '', customerPhone: a.customerPhone || '',
        workForName: a.workForName || '', workForPhone: a.workForPhone || '', workForRel: a.workForRel || null,
        eventType: a.eventType || '',
        address: a.address || '', notes: a.notes || '', lat: a.lat, lng: a.lng, projectRef: a.project || null,
      }))))
      .catch(() => setAssignments([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchAssignments, [currentWeekStart, calView]);

  // Employee HOME pins: geocode each home address once (client-side cache).
  const [empPins, setEmpPins] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const e of employees) {
        if (!e.homeAddr || empPins[e.id] !== undefined) continue;
        const pin = await geocodeToPin({ address: e.homeAddr });
        if (cancelled) return;
        setEmpPins(prev => ({ ...prev, [e.id]: pin.lat != null ? pin : null }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  // Which day column is highlighted. Defaults to TODAY when it's in view;
  // clicking any day header moves the highlight there.
  const [selectedDayIdx, setSelectedDayIdx] = useState(null);
  const manualDayShift = React.useRef(false);
  useEffect(() => {
    if (manualDayShift.current) { manualDayShift.current = false; return; }
    let idx = null;
    for (let i = 0; i < 7; i++) {
      if (isSameDay(addDays(currentWeekStart, i), new Date())) { idx = i; break; }
    }
    setSelectedDayIdx(idx);
  }, [currentWeekStart]);
  // Step the highlighted day ±1, rolling into the next/previous week as needed.
  const shiftSelectedDay = (delta) => {
    const base = selectedDayIdx != null ? addDays(currentWeekStart, selectedDayIdx) : new Date();
    const next = addDays(base, delta);
    const ws = startOfWeek(next, { weekStartsOn: 1 });
    if (!isSameDay(ws, currentWeekStart)) {
      manualDayShift.current = true;
      setCurrentWeekStart(ws);
    }
    setSelectedDayIdx(Math.round((next.getTime() - ws.getTime()) / 86400000));
  };

  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [hoveredAssignmentId, setHoveredAssignmentId] = useState(null);

  // Command key + Delete key handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.metaKey) setIsDuplicateMode(true);

      if ((e.key === 'Backspace' || e.key === 'Delete') && hoveredAssignmentId) {
        e.preventDefault();
        if (window.confirm('Delete this assignment?')) {
          deleteAssignment(hoveredAssignmentId);
        }
      }
    };

    const handleKeyUp = (e) => {
      if (!e.metaKey) setIsDuplicateMode(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [hoveredAssignmentId]);

  // Map expand: fills the page while the calendar hides; toggle collapses back.
  const [mapExpanded, setMapExpanded] = useState(false);
  const mapCardRef = React.useRef(null);
  // Clicking anywhere OUTSIDE the expanded map collapses it.
  useEffect(() => {
    if (!mapExpanded) return;
    const onDocClick = (e) => {
      if (mapCardRef.current && !mapCardRef.current.contains(e.target)) setMapExpanded(false);
    };
    // Arm on the NEXT tick so the click that expanded the map can never be
    // the click that collapses it.
    const t = setTimeout(() => document.addEventListener('click', onDocClick), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', onDocClick); };
  }, [mapExpanded]);

  // Inline edit inside the details card: null = viewing; else the draft fields.
  const [editDraft, setEditDraft] = useState(null); // { project, notes, date: 'yyyy-MM-dd' }

  // REAL projects for the edit card's PO search (same pattern as Create Invoice).
  const [allProjects, setAllProjects] = useState([]);
  const [projSearch, setProjSearch] = useState('');
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/projects`, { headers: { Authorization: token } })
      .then(r => setAllProjects((r.data || []).map(p => ({
        _id: p._id, poNumber: p.poNumber, title: p.title,
        customerName: p.customer?.name || '', customerPhone: p.customer?.primaryPhone || '',
        workForRel: p.workForRel || null, workForName: p.workForName || '', workForPhone: p.workForPhone || '',
        address: [p.jobSiteAddress?.street, p.jobSiteAddress?.city, p.jobSiteAddress?.state, p.jobSiteAddress?.zip].filter(Boolean).join(', '),
      }))))
      .catch(() => setAllProjects([]));
  }, []);
  // Relationships (sub-work senders) for the "Work for" picker. Admin-only
  // API — PMs just get no suggestions.
  const [relationships, setRelationships] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/relationships`, { headers: { Authorization: token } })
      .then(r => setRelationships((r.data || []).map(x => ({ id: x._id, company: x.company, phone: x.phone || '' }))))
      .catch(() => setRelationships([]));
  }, []);

  // Real customers for the Customer picker in Assign New Task.
  const [allCustomers, setAllCustomers] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/customers`, { headers: { Authorization: token } })
      .then(r => setAllCustomers((r.data || []).map(c => ({ id: c._id, name: c.name, phone: c.primaryPhone || '' }))))
      .catch(() => setAllCustomers([]));
  }, []);
  const [custPicked, setCustPicked] = useState('');

  // When the bar exactly holds the already-picked value, it's DISPLAY, not a
  // query — suppress dropdowns and Places calls until the user edits it.
  const newTaskProjectRef = React.useRef('');
  const pickedLabel = (editDraft?.project) || '';
  const barIsPicked = (v) => v.trim() !== '' && (v.trim() === pickedLabel.trim() || v.trim() === (newTaskProjectRef.current || '').trim());
  const filteredProjects = (projSearch.trim() && !barIsPicked(projSearch))
    ? allProjects.filter(p => {
        const q = projSearch.trim().toLowerCase();
        return String(p.poNumber || '').toLowerCase().includes(q) || String(p.title || '').toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  // CUSTOM ADDRESSES: once the query looks like an address (two-plus words,
  // e.g. "0104 Fake St"), Google Places suggestions join the PO results.
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  useEffect(() => {
    const q = projSearch.trim();
    const looksLikeAddress = q.split(/\s+/).length >= 2;
    if (!looksLikeAddress || barIsPicked(q)) { setAddrSuggestions([]); return; }
    const t = setTimeout(() => {
      getAddressSuggestions(q)
        .then(r => setAddrSuggestions((r.predictions || []).slice(0, 5)))
        .catch(() => setAddrSuggestions([]));
    }, 300); // debounce keystrokes
    return () => clearTimeout(t);
  }, [projSearch]);

  // The last 3 POs assigned through the picker — shown under an empty search
  // bar for one-tap reassignment (crews usually rotate between a few jobs).
  const [recentAssigned, setRecentAssigned] = useState([]); // [{ label, address }]
  const rememberAssigned = (label, address = '') =>
    setRecentAssigned(prev => [{ label, address }, ...prev.filter(r => r.label !== label)].slice(0, 3));
  // Seed from whatever's already on the calendar, newest-looking first.
  useEffect(() => {
    if (recentAssigned.length) return; // don't clobber user picks
    const seen = [];
    assignments.forEach(a => {
      if (a.project && !seen.some(r => r.label === a.project)) seen.push({ label: a.project, address: a.address || '' });
    });
    if (seen.length) setRecentAssigned(seen.slice(0, 3));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments]);
  // Optimistic local update + background save; refetch on failure.
  const patchAssignment = (assignmentId, localFields, serverFields) => {
    setAssignments(prev => prev.map(a => a.id === assignmentId ? { ...a, ...localFields } : a));
    const token = localStorage.getItem('token');
    axios.patch(`${API_BASE}/schedule-assignments/${assignmentId}`, serverFields, { headers: { Authorization: token } })
      .catch(() => { alert('Could not save the change.'); fetchAssignments(); });
  };
  // One exit for the card: whatever way it closes, the unsaved draft dies.
  const closeDetails = () => {
    setEditDraft(null);
    setProjSearch('');
    setSelectedAssignment(null);
  };

  // Tap/click-to-copy for every details-card value (except Notes) —
  // "Copied!" tooltip like CustomerProfile.
  const [copiedField, setCopiedField] = useState('');
  const copyVal = (text, field) => {
    try { navigator.clipboard.writeText(text); } catch (_) {}
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 3000);
  };
  const CopyText = ({ text, field, className = '' }) => text ? (
    <span onClick={() => copyVal(text, field)} title="Click to copy" className={`cursor-pointer relative inline-block ${className}`}>
      {text}
      {copiedField === field && (
        <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out whitespace-nowrap">Copied!</span>
      )}
    </span>
  ) : <span className="text-zinc-500">N/A</span>;

  // ---- "Send notification": opens Messages pre-filled for the employee ----
  // Auto-format phone typing → (916) 555-1234
  const fmtPhoneParen = (v) => {
    const d = String(v || '').replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  // "HH:mm" → "8:00AM"; '' stays ''. Used everywhere a time is displayed.
  const fmtTime = (t) => t ? format(new Date(`2000-01-01T${t}`), 'h:mmaaa').toUpperCase().replace(/\./g, '') : '';
  // Default end time = one hour past the start (start TBD → 9am).
  const plusOneHour = (t) => {
    const [h, m] = (t || '08:00').split(':').map(Number);
    return `${String((h + 1) % 24).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
  };
  const fmtStart = (a) => {
    const day = format(new Date(a.date), 'EEEE, MMMM d');
    return `${day} @ ${a.startTime ? fmtTime(a.startTime) : 'TBD'}`;
  };
  const notifBody = (a) => {
    // Blank line between each section — reads cleaner as a text message.
    const parts = [a.project];
    if (EVENT_TYPES[a.eventType]) parts.push(EVENT_TYPES[a.eventType].label);
    if (a.workForName && a.workForName !== 'Stalwart Electric') parts.push(`For: ${a.workForName}${a.workForPhone ? ` · ${a.workForPhone}` : ''}`);
    if (a.customerName) parts.push(`Customer: ${a.customerName}${a.customerPhone ? ` · ${a.customerPhone}` : ''}`);
    if (a.address && a.address !== a.project) parts.push(a.address);
    parts.push(`Start: ${fmtStart(a)}`);
    if (a.endTime) parts.push(`End: ${fmtTime(a.endTime)}`);
    if (a.notes) parts.push(`Notes:\n${a.notes}`);
    return parts.join('\n\n');
  };
  const smsHref = (phone, body) => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return null;
    return `sms:${digits}?&body=${encodeURIComponent(body)}`;
  };

  // Best-effort geocode → map pin. Never blocks a save; null just means no pin.
  const geocodeToPin = ({ placeId, address }) => new Promise((resolve) => {
    try {
      if (!window.google?.maps?.Geocoder) return resolve({ lat: null, lng: null });
      new window.google.maps.Geocoder().geocode(placeId ? { placeId } : { address }, (res, status) => {
        if (status === 'OK' && res?.[0]?.geometry?.location) {
          resolve({ lat: res[0].geometry.location.lat(), lng: res[0].geometry.location.lng() });
        } else resolve({ lat: null, lng: null });
      });
    } catch (_) { resolve({ lat: null, lng: null }); }
  });

  const saveEditDraft = () => {
    if (!selectedAssignment || !editDraft) return;
    const fields = {
      project: editDraft.project.trim() || selectedAssignment.project,
      notes: editDraft.notes,
      date: new Date(`${editDraft.date}T00:00:00`),
      address: editDraft.address != null ? editDraft.address : (selectedAssignment.address || ''),
      startTime: editDraft.hasStart ? (editDraft.startTime || '') : '',
      endTime: editDraft.hasEnd ? (editDraft.endTime || '') : '',
      customerName: (editDraft.customerName || '').trim(),
      customerPhone: (editDraft.customerPhone || '').trim(),
      workForName: (editDraft.workForName || '').trim() || 'Stalwart Electric',
      workForPhone: (editDraft.workForPhone || '').trim(),
      eventType: editDraft.eventType || '',
      projectRef: editDraft.projectRef !== undefined ? editDraft.projectRef : (selectedAssignment.projectRef || null),
      lat: editDraft.lat !== undefined ? editDraft.lat : selectedAssignment.lat,
      lng: editDraft.lng !== undefined ? editDraft.lng : selectedAssignment.lng,
    };
    patchAssignment(selectedAssignment.id, fields, {
      projectLabel: fields.project, notes: fields.notes, date: fields.date, startTime: fields.startTime,
      endTime: fields.endTime, customerName: fields.customerName, customerPhone: fields.customerPhone,
      workForName: fields.workForName, workForPhone: fields.workForPhone, eventType: fields.eventType,
      address: fields.address, project: fields.projectRef, lat: fields.lat, lng: fields.lng,
    });
    setSelectedAssignment(prev => ({ ...prev, ...fields }));
    setEditDraft(null);
    setProjSearch('');
  };

  const deleteAssignment = (assignmentId) => {
    setAssignments(prev => prev.filter(a => a.id !== assignmentId));
    if (selectedAssignment?.id === assignmentId) setSelectedAssignment(null);
    setHoveredAssignmentId(null);
    const token = localStorage.getItem('token');
    axios.delete(`${API_BASE}/schedule-assignments/${assignmentId}`, { headers: { Authorization: token } })
      .catch(() => { alert('Could not delete — restoring.'); fetchAssignments(); });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // PO number for a task's linked project (N/A when it has none).
  const poOf = (a) => {
    const rid = a.projectRef?._id || a.projectRef;
    if (!rid) return null;
    const p = allProjects.find(x => x._id === rid);
    return p?.poNumber || null;
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    // over.id = "<mongoEmployeeId>-<dayIdx>" (Mongo ids contain no dashes).
    const [targetEmpId, targetDayIdxStr] = over.id.split('-');
    const targetDayIdx = parseInt(targetDayIdxStr);
    const targetDate = weekDays[targetDayIdx];
    const src = assignments.find(a => a.id === active.id);
    if (!src) return;
    const token = localStorage.getItem('token');

    if (isDuplicateMode) {
      // ⌘-drag: create a copy on the target cell.
      const tempId = `temp-${Date.now()}`;
      setAssignments(prev => [...prev, { ...src, id: tempId, employeeId: targetEmpId, date: targetDate }]);
      axios.post(`${API_BASE}/schedule-assignments`, {
        employee: targetEmpId, date: targetDate, projectLabel: src.project,
        startTime: src.startTime || '', endTime: src.endTime || '', customerName: src.customerName || '',
        customerPhone: src.customerPhone || '', workForName: src.workForName || '', workForPhone: src.workForPhone || '', workForRel: src.workForRel || null,
        eventType: src.eventType || '',
        project: src.projectRef, address: src.address, notes: src.notes, lat: src.lat, lng: src.lng,
      }, { headers: { Authorization: token } })
        .then(r => setAssignments(prev => prev.map(a => a.id === tempId ? { ...a, id: r.data._id } : a)))
        .catch(() => { alert('Could not duplicate the assignment.'); fetchAssignments(); });
    } else {
      patchAssignment(src.id, { employeeId: targetEmpId, date: targetDate }, { employee: targetEmpId, date: targetDate });
    }
  };

  const showDetails = (assignment, employeeName) => {
    setSelectedAssignment({ ...assignment, employeeName });
  };

  // ---- "+ Assign New Task" modal ----
  const [showNewTask, setShowNewTask] = useState(false);
  // Recents only appear once the project bar is actually focused.
  const [projBarFocused, setProjBarFocused] = useState(false);
  const blurProjBar = () => setTimeout(() => setProjBarFocused(false), 200); // let recent-clicks land
  // Work for collapses once a PO covers it; this re-expands it for referrals.
  const [wfExpanded, setWfExpanded] = useState(false);
  const emptyTask = () => ({
    employee: '', date: format(new Date(), 'yyyy-MM-dd'),
    hasStart: true, startTime: '08:00', hasEnd: false, endTime: '',
    customerName: '', customerPhone: '', eventType: '',
    workForName: '', workForPhone: '', workForRel: null,
    project: '', address: '', projectRef: null, lat: null, lng: null, notes: '',
  });
  const [newTask, setNewTask] = useState(emptyTask());
  const openNewTask = () => { setNewTask(emptyTask()); setProjSearch(''); newTaskProjectRef.current = ''; setWfExpanded(false); setShowNewTask(true); };
  // Right-click any calendar date → small menu → Assign New Task with THAT
  // date pre-filled (the + button still defaults to today).
  const [dayMenu, setDayMenu] = useState(null); // { x, y, date }
  const openDayMenu = (e, day) => {
    e.preventDefault();
    e.stopPropagation();
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 70);
    setDayMenu({ x, y, date: day });
  };
  const openNewTaskOn = (day) => {
    setNewTask({ ...emptyTask(), date: format(day, 'yyyy-MM-dd') });
    setProjSearch(''); newTaskProjectRef.current = ''; setWfExpanded(false);
    setShowNewTask(true);
  };
  const closeNewTask = () => { setShowNewTask(false); setProjSearch(''); newTaskProjectRef.current = ''; };
  const saveNewTask = () => {
    if (!newTask.employee) { alert('Pick an employee.'); return; }
    if (!newTask.project.trim() && !newTask.workForName.trim()) { alert('Pick a project/address, or a company under "Work for".'); return; }
    // Block label priority (the address has its own line; the visit KIND is
    // the badge's job now — no "— Service Call" suffix in the name):
    //   PO title > sub-work company > customer name > address.
    const bareAddress = !newTask.project.trim() || newTask.project.trim() === newTask.address.trim();
    const label = (newTask.workForName.trim() && bareAddress)
      ? newTask.workForName.trim()
      : (newTask.customerName.trim() && bareAddress)
        ? newTask.customerName.trim()
        : newTask.project.trim();
    const token = localStorage.getItem('token');
    axios.post(`${API_BASE}/schedule-assignments`, {
      employee: newTask.employee,
      date: new Date(`${newTask.date}T00:00:00`),
      startTime: newTask.hasStart ? (newTask.startTime || '') : '',
      endTime: newTask.hasEnd ? (newTask.endTime || '') : '',
      customerName: newTask.customerName.trim(),
      customerPhone: newTask.customerPhone.trim(),
      // No Work for = our own job — stamp it as such.
      workForName: newTask.workForName.trim() || 'Stalwart Electric',
      workForPhone: newTask.workForPhone.trim(),
      workForRel: newTask.workForRel,
      eventType: newTask.eventType,
      projectLabel: label,
      project: newTask.projectRef,
      address: newTask.address,
      notes: newTask.notes,
      lat: newTask.lat, lng: newTask.lng,
    }, { headers: { Authorization: token } })
      .then(() => { closeNewTask(); fetchAssignments(); })
      .catch((e) => alert(e.response?.data?.error || 'Could not create the assignment.'));
  };

  // Shared by the inline (desktop) and fullscreen (mobile) maps.
  // ---- Click-a-dot routing (OSRM, free/keyless): home → jobsite ----
  const [routes, setRoutes] = useState([]); // [{ positions, color, name, project, miles, mins }]
  useEffect(() => { setRoutes([]); }, [selectedDayIdx, currentWeekStart, altSelectedDate]); // day change clears routes
  const fetchDriveRoute = async (from, to) => {
    const url = `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
    const r = await fetch(url);
    const j = await r.json();
    const route = j.routes && j.routes[0];
    if (!route) throw new Error('no route');
    return {
      positions: route.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
      miles: route.distance / 1609.344,
      mins: route.duration / 60,
    };
  };
  // An employee's pinned jobs for the visible day, in start-time order.
  const empDayJobs = (empId) => mapAssignments
    .filter(a => a.employeeId === empId && a.lat != null && a.lng != null)
    .sort((x, y) => (x.startTime || '99') < (y.startTime || '99') ? -1 : 1);

  // Toggle ONE leg of an employee's day. legKey identifies it (emp + leg #).
  const toggleLeg = async (emp, legKey, from, to, project) => {
    if (routes.some(r => r.key === legKey)) {
      setRoutes(prev => prev.filter(r => r.key !== legKey)); // toggle OFF just this leg
      return;
    }
    try {
      const r = await fetchDriveRoute(from, to);
      setRoutes(prev => [
        ...prev.filter(x => x.key !== legKey),
        { ...r, key: legKey, color: emp.color, name: emp.name, project },
      ]);
    } catch (_) { /* routing service hiccup — just no line */ }
  };

  // Home ring: leg 0 (home → first job of the day).
  const routeEmployee = (emp) => {
    const home = empPins[emp.id];
    if (!home) return;
    const jobs = empDayJobs(emp.id);
    if (!jobs.length) return;
    toggleLeg(emp, `${emp.id}#0`, [home.lat, home.lng], [jobs[0].lat, jobs[0].lng], jobs[0].project);
  };

  // Close any open Leaflet popup so the route is viewed unobstructed.
  const closeMapPopups = () =>
    document.querySelectorAll('.leaflet-popup-close-button').forEach(b => b.click());

  // Full day: home → job1 → job2 → … all legs at once. Clicking again while
  // the full route is up clears this employee's legs.
  const routeFullDay = async (emp) => {
    const home = empPins[emp.id];
    const jobs = empDayJobs(emp.id);
    if (!home || !jobs.length) return;
    const mine = routes.filter(r => r.key.startsWith(`${emp.id}#`));
    if (mine.length >= jobs.length) {
      setRoutes(prev => prev.filter(r => !r.key.startsWith(`${emp.id}#`)));
      return;
    }
    const pts = [[home.lat, home.lng], ...jobs.map(j => [j.lat, j.lng])];
    const legs = [];
    for (let i = 0; i + 1 < pts.length; i++) {
      try {
        const r = await fetchDriveRoute(pts[i], pts[i + 1]);
        legs.push({ ...r, key: `${emp.id}#${i}`, color: emp.color, name: emp.name, project: jobs[i].project });
      } catch (_) { /* routing hiccup — skip that leg */ }
    }
    setRoutes(prev => [...prev.filter(r => !r.key.startsWith(`${emp.id}#`)), ...legs]);
  };

  // Jobsite dot: for each worker there, draw their NEXT leg (this job → the
  // following job of their day) — chain stop to stop with each double-click.
  const routeNextLegs = (site) => {
    const seen = new Set();
    site.entries.forEach(({ a, emp }) => {
      if (!emp || seen.has(`${emp.id}:${a.id}`)) return;
      seen.add(`${emp.id}:${a.id}`);
      const jobs = empDayJobs(emp.id);
      const idx = jobs.findIndex(j => j.id === a.id);
      if (idx >= 0 && idx + 1 < jobs.length) {
        const next = jobs[idx + 1];
        toggleLeg(emp, `${emp.id}#${idx + 1}`, [a.lat, a.lng], [next.lat, next.lng], next.project);
      }
    });
  };

  // Month/Year views select a day directly (single click) — the map follows
  // it without leaving the view. Week view keeps its column-index selection.
  const selectedDate = calView !== 'week'
    ? altSelectedDate
    : (selectedDayIdx != null ? addDays(currentWeekStart, selectedDayIdx) : null);
  const mapAssignments = selectedDate
    ? assignments.filter(a => isSameDay(new Date(a.date), selectedDate))
    : assignments;
  const mapHomeEmployees = (selectedDate
    ? employees.filter(e => mapAssignments.some(a => a.employeeId === e.id))
    : employees
  ).filter(e => empPins[e.id]);
  const mapPoints = [
    ...mapAssignments.filter(a => a.lat != null && a.lng != null).map(a => [a.lat, a.lng]),
    ...mapHomeEmployees.map(e => [empPins[e.id].lat, empPins[e.id].lng]),
  ];
  // Same-coordinate jobsites grouped: one dot, pie-sliced with every
  // assigned employee's color (the Finder multi-tag look).
  // Touch screens get bigger dots — a 16px target under a finger is misery.
  const coarsePointer = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const HOME_SZ = coarsePointer ? 22 : 14;
  const SITE_SZ = coarsePointer ? 26 : 16;

  const jobSites = Object.values(
    mapAssignments.filter(a => a.lat != null && a.lng != null).reduce((acc, a) => {
      const key = `${a.lat.toFixed(5)},${a.lng.toFixed(5)}`;
      if (!acc[key]) acc[key] = { lat: a.lat, lng: a.lng, entries: [] };
      acc[key].entries.push({ a, emp: employees.find(e => e.id === a.employeeId) });
      return acc;
    }, {})
  );
  const pieBackground = (colors) => {
    if (colors.length <= 1) return colors[0] || '#ec4899';
    const seg = 100 / colors.length;
    return `conic-gradient(${colors.map((c, i) => `${c} ${i * seg}% ${(i + 1) * seg}%`).join(', ')})`;
  };

  // Plain JSX (NOT a component): keeps the map children's identity stable
  // across re-renders, so tiles never remount/blink.
  const mapLayers = (
    <>
      <FitToPins points={mapPoints} />
      <MapClickClear onClear={() => setRoutes([])} />
              {/* Standard keyless OSM tiles, dark-themed by a CSS filter (the
                  .map-tiles-dark class below inverts them) — no API key, no
                  third-party tier to get yanked. Drop the className for the
                  normal colorful map. */}
              <TileLayer
                className="map-tiles-dark"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {/* Employee HOMES — hollow ring in the employee's color. */}
              {mapHomeEmployees.map(e => (
                <Marker
                  key={`home-${e.id}`}
                  position={[empPins[e.id].lat, empPins[e.id].lng]}
                  eventHandlers={{ dblclick: (ev) => { L.DomEvent.stopPropagation(ev.originalEvent); routeEmployee(e); } }}
                  icon={L.divIcon({
                    className: 'custom-marker',
                    html: `<div style="background-color:#111827;width:${HOME_SZ}px;height:${HOME_SZ}px;border-radius:50%;border:${coarsePointer ? 4 : 3}px solid ${e.color};box-shadow:0 0 4px rgba(0,0,0,0.5);"></div>`,
                    iconSize: [HOME_SZ, HOME_SZ],
                    iconAnchor: [HOME_SZ / 2, HOME_SZ / 2]
                  })}
                >
                  <Popup className="text-sm font-medium">
                    {e.name}<br />
                    <span className="text-xs text-zinc-500">Home{e.role ? ` · ${e.role}` : ''}</span>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button
                        onClick={() => { routeEmployee(e); closeMapPopups(); }}
                        style={{ background: '#1f2937', border: `1px solid ${e.color}`, color: '#e5e7eb', borderRadius: 9999, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Show Route
                      </button>
                      <button
                        onClick={() => { routeFullDay(e); closeMapPopups(); }}
                        style={{ background: e.color, border: `1px solid ${e.color}`, color: '#111827', borderRadius: 9999, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Full Route
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* One dot per SITE; shared sites wear every crew color as pie
                  slices. Click a dot to draw each worker's home→site route. */}
              {jobSites.map(site => {
                const colors = [...new Set(site.entries.map(en => en.emp?.color || '#ec4899'))];
                return (
                  <Marker
                    key={`${site.lat},${site.lng}`}
                    position={[site.lat, site.lng]}
                    eventHandlers={{ dblclick: (ev) => { L.DomEvent.stopPropagation(ev.originalEvent); routeNextLegs(site); } }}
                    icon={L.divIcon({
                      className: 'custom-marker',
                      html: `<div style="background:${pieBackground(colors)};width:${SITE_SZ}px;height:${SITE_SZ}px;border-radius:50%;border:2px solid #4b5563;box-shadow:0 0 4px rgba(0,0,0,0.5);"></div>`,
                      iconSize: [SITE_SZ, SITE_SZ],
                      iconAnchor: [SITE_SZ / 2, SITE_SZ / 2]
                    })}
                  >
                    <Popup className="text-sm font-medium">
                      {site.entries[0].a.project}<br />
                      {site.entries.map(en => (
                        <span key={en.a.id} className="text-xs text-zinc-500 block">
                          {en.emp ? en.emp.name : '—'} · {format(new Date(en.a.date), 'MMM d')}{en.a.startTime ? ` @ ${format(new Date(`2000-01-01T${en.a.startTime}`), 'h:mmaaa').toUpperCase()}` : ''}
                        </span>
                      ))}
                    </Popup>
                  </Marker>
                );
              })}
              {/* Route lines (home → jobsite), one per clicked employee */}
              {routes.map(r => (
                <Polyline key={r.key} positions={r.positions} pathOptions={{ color: r.color, weight: 4, opacity: 0.85 }} />
              ))}
    </>
  );

  const [showMobileMap, setShowMobileMap] = useState(false);

  // Distance/ETA chip for the drawn routes (rendered inside both map surfaces).
  const routeChip = routes.length > 0 ? (
    <div className="absolute bottom-3 left-3 z-[1001] space-y-1 max-w-[85%]">
      {routes.map(r => (
        <div key={r.key} className="px-3 py-1.5 rounded-xl bg-gray-900/90 border border-gray-700 text-xs text-zinc-200 flex items-center gap-2 shadow-lg">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
          <span className="truncate">{r.name} → {r.project}</span>
          <span className="font-bold shrink-0">{r.miles.toFixed(1)} mi · ~{Math.round(r.mins)} min</span>
        </div>
      ))}
      <button
        onClick={() => setRoutes([])}
        className="px-2.5 py-1 rounded-lg bg-gray-900/90 border border-gray-700 text-[10px] text-zinc-400 hover:text-white"
      >
        ✕ Clear routes
      </button>
    </div>
  ) : null;

  const goToToday = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  // Prev/Next move by whatever the calendar is showing: week, month, or year.
  const prevWeek = () => setCurrentWeekStart(prev =>
    calView === 'month' ? addMonths(prev, -1) : calView === 'year' ? addYears(prev, -1) : addDays(prev, -7));
  const nextWeek = () => setCurrentWeekStart(prev =>
    calView === 'month' ? addMonths(prev, 1) : calView === 'year' ? addYears(prev, 1) : addDays(prev, 7));

  // Year view → click a day → land on that WEEK with that day highlighted
  // (which also points the map at it, as usual).
  const jumpToDay = (day) => {
    const ws = startOfWeek(day, { weekStartsOn: 1 });
    manualDayShift.current = true;
    setCurrentWeekStart(ws);
    for (let i = 0; i < 7; i++) {
      if (isSameDay(addDays(ws, i), day)) { setSelectedDayIdx(i); break; }
    }
    setCalView('week');
  };

  // day-key → that day's assignments (month chips + year dots read this).
  const assignmentsByDay = React.useMemo(() => {
    const m = {};
    assignments.forEach(a => {
      const k = format(new Date(a.date), 'yyyy-MM-dd');
      (m[k] = m[k] || []).push(a);
    });
    return m;
  }, [assignments]);

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-6 max-md:p-3 space-y-6 max-md:space-y-4 max-md:overflow-x-hidden">
      {/* Sticky top navigation */}
      <div className="sticky top-0 z-20 bg-gray-950 pb-4 border-b border-gray-800">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-3xl max-md:text-xl font-bold tracking-tight">Employee Scheduling</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMobileMap(true)}
              className="md:hidden px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm font-medium text-zinc-300 flex items-center gap-1.5"
            >
              <Maximize2 size={13} /> Map
            </button>
            <button onClick={openNewTask} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors">
              + Assign New Task
            </button>
          </div>
        </div>
      </div>

      {/* Week label + compact nav, hugging the table's top edge.
          Hidden entirely while the map is expanded. */}
      {!mapExpanded && (
      <div>
        <div className="flex flex-wrap justify-between items-end gap-y-1.5 mb-1.5">
          <div className="text-sm text-zinc-400 flex items-center gap-2">
            <button
              onClick={cycleCalView}
              title="Click to switch between week, month, and year views"
              className="px-2.5 py-1 bg-gray-800 hover:bg-gray-700 rounded-md text-xs font-medium text-zinc-300 transition-colors"
            >
              {calView === 'week' ? 'Week of' : calView === 'month' ? 'Month of' : 'Year'}
            </button>
            <span className="text-zinc-200 font-semibold">
              {calView === 'week' ? format(currentWeekStart, 'MMM d, yyyy')
                : calView === 'month' ? format(currentWeekStart, 'MMMM yyyy')
                : format(currentWeekStart, 'yyyy')}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={prevWeek} className="px-2.5 py-1 bg-gray-800 hover:bg-gray-700 rounded-md text-xs font-medium text-zinc-300 transition-colors">
              ← Prev
            </button>
            <button onClick={goToToday} className="px-2.5 py-1 bg-gray-800 hover:bg-gray-700 rounded-md text-xs font-medium text-zinc-300 transition-colors">
              Today
            </button>
            <button onClick={nextWeek} className="px-2.5 py-1 bg-gray-800 hover:bg-gray-700 rounded-md text-xs font-medium text-zinc-300 transition-colors">
              Next →
            </button>
          </div>
        </div>

      {calView === 'week' && (<>
      {/* Scrollable calendar — DESKTOP ONLY (phones get day cards below) */}
      <div className="overflow-x-auto pb-4 max-md:hidden">
        <div className="min-w-[1200px]">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
              <div className="grid grid-cols-[200px_repeat(7,1fr)]">
                <div className="bg-gray-950/70 p-4 font-semibold text-zinc-400 border-b border-gray-800 sticky left-0 z-10">
                  Employee
                </div>
                {weekDays.map((day, idx) => {
                  const today = isSameDay(day, new Date());
                  const selected = selectedDayIdx === idx;
                  return (
                  <div
                    key={idx}
                    onClick={() => setSelectedDayIdx(idx)}
                    onContextMenu={(e) => openDayMenu(e, day)}
                    title="Click to highlight this day"
                    className={`p-4 text-center font-medium border-b border-gray-800 cursor-pointer transition-colors
                      ${selected ? (today ? 'bg-indigo-500/10' : 'bg-white/[0.07]') : 'hover:bg-white/[0.03]'}
                      ${today ? 'border-t-2 border-t-indigo-400/70 text-indigo-200' : ''}`}
                  >
                    {format(day, 'EEE')}{today ? ' (Today)' : ''}<br />
                    <span className={`text-sm ${today ? 'text-indigo-300' : 'text-zinc-500'}`}>{format(day, 'MMM d')}</span>
                  </div>
                  );
                })}

                {employees.map(emp => (
                  <React.Fragment key={emp.id}>
                    <div className="p-4 border-b border-r border-gray-800 flex items-center gap-3 bg-gray-950/30 sticky left-0 z-10">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: emp.color }}></div>
                      <div>
                        <div className="font-medium">{emp.name}</div>
                        <div className="text-xs text-zinc-500">{emp.role}</div>
                      </div>
                    </div>

                    {weekDays.map((day, dayIdx) => {
                      const dayAssignments = assignments
                        .filter(a => a.employeeId === emp.id && isSameDay(new Date(a.date), day))
                        .sort((x, y) => (x.startTime || '99').localeCompare(y.startTime || '99'));

                      return (
                        <DroppableDay key={dayIdx} id={`${emp.id}-${dayIdx}`} isSelected={selectedDayIdx === dayIdx} isToday={isSameDay(day, new Date())} onContextMenu={(e) => openDayMenu(e, day)}>
                          {dayAssignments.map(assignment => (
                            <AssignmentBlock
                              key={assignment.id}
                              id={assignment.id}
                              assignment={assignment}
                              notifHref={smsHref(emp.phone, notifBody(assignment))}
                              po={poOf(assignment)}
                              color={emp.color}
                              onClick={() => showDetails(assignment, emp.name)}
                              onHoverChange={setHoveredAssignmentId}
                              isDuplicateMode={isDuplicateMode}
                            />
                          ))}
                        </DroppableDay>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </DndContext>
        </div>
      </div>

      {/* MOBILE: the week as day-by-day cards — no giant table to pan. */}
      <div className="md:hidden space-y-4">
        {weekDays.map((day, di) => {
          const dayAss = assignments
            .filter(a => isSameDay(new Date(a.date), day))
            .sort((x, y) => (x.startTime || '99').localeCompare(y.startTime || '99'));
          const today = isSameDay(day, new Date());
          return (
            <div key={di} onContextMenu={(e) => openDayMenu(e, day)}>
              <div className={`flex items-center gap-2 mb-1.5 ${today ? 'text-indigo-300' : 'text-zinc-500'}`}>
                <span className="text-xs font-bold uppercase tracking-widest">{format(day, 'EEE · MMM d')}</span>
                {today && <span className="text-[10px] bg-indigo-500/20 px-1.5 py-0.5 rounded-full font-semibold">Today</span>}
                <div className="flex-1 h-px bg-zinc-800/60" />
              </div>
              {dayAss.length === 0 ? (
                <p className="text-xs text-zinc-700 mb-1">—</p>
              ) : dayAss.map(a => {
                const emp = employees.find(e => e.id === a.employeeId);
                const href = smsHref(emp?.phone, notifBody(a));
                return (
                  <div
                    key={a.id}
                    onClick={() => showDetails(a, emp?.name || '')}
                    style={{ borderLeft: `4px solid ${emp?.color || '#666'}` }}
                    className="bg-gray-900 border border-gray-800/60 rounded-xl p-3 mb-2 active:bg-gray-800/70"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-semibold text-sm text-zinc-100">{emp?.name || '—'}</span>
                      <span className="text-right shrink-0">
                        {a.startTime && (
                          <span className="block text-xs font-semibold text-indigo-300">
                            {format(new Date(`2000-01-01T${a.startTime}`), 'h:mmaaa').toUpperCase()}
                          </span>
                        )}
                        <span className="block text-[9px] font-bold text-zinc-500">PO: {poOf(a) || 'N/A'}</span>
                      </span>
                    </div>
                    <p className="text-sm text-zinc-300 mt-0.5 break-words">{a.project}</p>
                    {a.notes && <p className="text-xs text-zinc-500 mt-0.5 break-words">{a.notes}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      <EventBadge type={a.eventType} />
                      {href ? (
                        <a
                          href={href}
                          onClick={(e) => e.stopPropagation()}
                          title="Notify Employee"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600/20 border border-indigo-500/40 text-indigo-300"
                        >
                          <Send size={13} />
                        </a>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); alert(`No phone on file for ${emp?.name || 'this employee'} — add one in their profile.`); }}
                          title="Notify Employee (no phone on file)"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-800 border border-gray-700 text-zinc-500"
                        >
                          <Send size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      </>)}

      {/* MONTH VIEW — full month grid; each day lists mini chips (employee
          names). Clicking a chip opens the usual Assignment Details card. */}
      {calView === 'month' && (() => {
        const monthStart = startOfMonth(currentWeekStart);
        const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
        return (
          <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
            <div className="grid grid-cols-7 border-b border-gray-800 bg-gray-950/70">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <div key={d} className="p-2 text-center text-[11px] max-md:text-[9px] font-bold uppercase tracking-wider text-zinc-500">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((day, i) => {
                const inMonth = isSameMonth(day, monthStart);
                const today = isSameDay(day, new Date());
                const dayAss = (assignmentsByDay[format(day, 'yyyy-MM-dd')] || [])
                  .slice()
                  .sort((x, y) => (x.startTime || '99').localeCompare(y.startTime || '99'));
                const isSel = selectedDate && isSameDay(day, selectedDate);
                return (
                  <div
                    key={i}
                    onClick={() => setAltSelectedDate(day)}
                    onDoubleClick={() => jumpToDay(day)}
                    onContextMenu={(e) => openDayMenu(e, day)}
                    title="Click: show this day on the map · Double-click: open its week"
                    className={`min-h-[92px] max-md:min-h-[64px] border-b border-r border-gray-800/60 p-1.5 max-md:p-1 cursor-pointer ${inMonth ? '' : 'bg-gray-950/50'} ${today ? 'bg-indigo-500/10' : isSel ? 'bg-white/[0.06]' : ''} ${isSel ? 'ring-1 ring-inset ring-indigo-500/50' : ''}`}
                  >
                    <p className={`text-[11px] max-md:text-[9px] font-semibold mb-1 ${today ? 'text-indigo-300' : inMonth ? 'text-zinc-400' : 'text-zinc-700'}`}>
                      {format(day, 'd')}
                    </p>
                    {dayAss.map(a => {
                      const emp = employees.find(e => e.id === a.employeeId);
                      return (
                        <button
                          key={a.id}
                          onClick={(e) => { e.stopPropagation(); showDetails(a, emp?.name || ''); }}
                          onDoubleClick={(e) => e.stopPropagation()}
                          title={`${emp?.name || '—'} — ${a.project}`}
                          style={{ borderLeft: `3px solid ${emp?.color || '#666'}` }}
                          className="block w-full text-left bg-gray-800/70 hover:bg-gray-700/80 rounded px-1.5 py-0.5 mb-1 text-[10px] max-md:text-[8px] font-semibold text-zinc-200 truncate"
                        >
                          {emp?.name || '—'}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* YEAR VIEW — 12 mini-months; scheduled days wear crew-colored dots.
          Hover a day for who's on it; click to jump to that week (day
          highlighted, map follows). */}
      {calView === 'year' && (() => {
        const yearStart = startOfYear(currentWeekStart);
        return (
          <div className="grid grid-cols-4 max-md:grid-cols-2 gap-4">
            {Array.from({ length: 12 }, (_, m) => addMonths(yearStart, m)).map((monthStart, mi) => {
              const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
              const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
              return (
                <div key={mi} className="bg-gray-900 rounded-xl border border-gray-800/50 p-3 max-md:p-2">
                  <p className="text-xs font-bold text-zinc-300 mb-2 text-center">{format(monthStart, 'MMMM')}</p>
                  <div className="grid grid-cols-7 gap-y-0.5">
                    {cells.map((day, i) => {
                      if (!isSameMonth(day, monthStart)) return <span key={i} />;
                      const dayAss = assignmentsByDay[format(day, 'yyyy-MM-dd')] || [];
                      const colors = [...new Set(dayAss.map(a => employees.find(e => e.id === a.employeeId)?.color || '#818cf8'))].slice(0, 3);
                      const names = [...new Set(dayAss.map(a => employees.find(e => e.id === a.employeeId)?.name || '—'))].join(', ');
                      const today = isSameDay(day, new Date());
                      const isSel = selectedDate && isSameDay(day, selectedDate);
                      return (
                        <button
                          key={i}
                          onClick={() => setAltSelectedDate(day)}
                          onDoubleClick={() => jumpToDay(day)}
                          onContextMenu={(e) => openDayMenu(e, day)}
                          title={`${names || format(day, 'MMM d')} · click: map · double-click: open week`}
                          className={`flex flex-col items-center rounded py-0.5 hover:bg-gray-800/80 ${today ? 'bg-indigo-500/15' : isSel ? 'bg-white/[0.08]' : ''} ${isSel ? 'ring-1 ring-indigo-500/60' : ''}`}
                        >
                          <span className={`text-[9px] leading-tight ${today ? 'text-indigo-300 font-bold' : dayAss.length ? 'text-zinc-200 font-semibold' : 'text-zinc-600'}`}>
                            {format(day, 'd')}
                          </span>
                          <span className="flex gap-[2px] h-[5px] mt-[1px]">
                            {colors.map((c, ci) => (
                              <span key={ci} className="w-[4px] h-[4px] rounded-full" style={{ backgroundColor: c }} />
                            ))}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
      </div>
      )}

      {/* Live map — full landscape width, always visible under the calendar */}
      <style>{`
        .map-tiles-dark { filter: var(--map-tiles-filter, invert(100%) hue-rotate(180deg) brightness(95%) contrast(90%) grayscale(35%)); }
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          20% { opacity: 1; transform: translateY(0); }
          80% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        .animate-fade-in-out { animation: fadeInOut 3s ease-in-out forwards; }
      `}</style>
      {/* relative z-0 CAGES Leaflet's internal z-indexes (400–1000) inside this
          card, so the map can never paint over popovers elsewhere on the page. */}
      <div ref={mapCardRef} className="max-md:hidden relative z-0 mt-6 bg-gray-900 rounded-2xl overflow-hidden border border-gray-800/50 shadow-2xl">
        {/* Expand/collapse — sits above Leaflet's panes (z up to ~1000) */}
        <button
          onClick={(e) => { e.stopPropagation(); setMapExpanded(v => !v); }}
          title={mapExpanded ? 'Collapse map' : 'Expand map'}
          className="absolute top-3 right-3 z-[1001] p-2.5 bg-gray-900/85 hover:bg-gray-800 border border-gray-700 rounded-xl text-zinc-300 hover:text-white shadow-lg transition-colors"
        >
          {mapExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        {routeChip}
        <div className={mapExpanded ? 'h-[calc(100dvh-160px)] w-full' : 'h-[420px] max-md:h-[300px] w-full'}>
            <MapContainer
              key={mapExpanded ? 'expanded' : 'inline'} /* remount so Leaflet measures the new size */
              center={[38.5816, -121.4944]}
              zoom={11}
              style={{ height: '100%', width: '100%' }}
            >
              {mapLayers}
            </MapContainer>
        </div>
      </div>

      {showMobileMap && (
        <div className="md:hidden fixed inset-0 z-[120] bg-gray-950 flex flex-col">
          {/* Day stepper: ← Prev · Today, Wed, Sep 23 · Next → */}
          <div className="relative z-[1002] h-14 shrink-0 flex items-center justify-between px-3 bg-gray-950 border-b border-gray-800">
            <button onClick={() => shiftSelectedDay(-1)} className="px-3 py-1.5 bg-gray-800 rounded-lg text-xs font-semibold text-zinc-300">
              ← Prev
            </button>
            <span className="text-sm font-bold text-zinc-100">
              {(() => {
                const d = selectedDayIdx != null ? addDays(currentWeekStart, selectedDayIdx) : null;
                if (!d) return 'This Week';
                return `${isSameDay(d, new Date()) ? 'Today, ' : ''}${format(d, 'EEE, MMM d')}`;
              })()}
            </span>
            <button onClick={() => shiftSelectedDay(1)} className="px-3 py-1.5 bg-gray-800 rounded-lg text-xs font-semibold text-zinc-300">
              Next →
            </button>
          </div>
          <div className="flex-1 relative">
          {routeChip}
          <button
            onClick={() => setShowMobileMap(false)}
            className="absolute top-4 right-4 z-[1001] p-3 bg-gray-900/90 border border-gray-700 rounded-full text-zinc-200 shadow-lg"
          >
            <Minimize2 size={18} />
          </button>
          <MapContainer
            key="mobile-full"
            center={[38.5816, -121.4944]}
            zoom={11}
            style={{ height: '100%', width: '100%' }}
          >
            {mapLayers}
          </MapContainer>
          </div>
        </div>
      )}

      {/* ---- Right-click day menu ---- */}
      {dayMenu && (
        <div
          className="fixed inset-0 z-[60]"
          onClick={() => setDayMenu(null)}
          onContextMenu={(e) => { e.preventDefault(); setDayMenu(null); }}
        >
          <div
            className="absolute bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-1 min-w-[180px]"
            style={{ left: dayMenu.x, top: dayMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              {format(dayMenu.date, 'EEE, MMM d')}
            </p>
            <button
              onClick={() => { const d = dayMenu.date; setDayMenu(null); openNewTaskOn(d); }}
              className="block w-full text-left px-3 py-1.5 text-sm text-zinc-200 hover:bg-gray-800"
            >
              ＋ Assign New Task
            </button>
          </div>
        </div>
      )}

      {/* ---- New Task Modal ---- */}
      {showNewTask && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeNewTask}>
          <div className="bg-gray-900 rounded-2xl p-8 max-md:p-5 max-w-lg w-full border border-gray-700 shadow-2xl max-h-[90dvh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Assign New Task</h2>
            <div className="space-y-4">
              <div>
                <p className="text-zinc-400 text-sm mb-1">Employee</p>
                <select
                  value={newTask.employee}
                  onChange={(e) => setNewTask(t => ({ ...t, employee: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-indigo-500"
                >
                  <option value="">Select an employee…</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}{e.role ? ` — ${e.role}` : ''}</option>)}
                </select>
              </div>
              <div>
                <p className="text-zinc-400 text-sm mb-1">Project / Jobsite</p>
                <input
                  value={projSearch}
                  onChange={(e) => setProjSearch(e.target.value)}
                  onFocus={() => setProjBarFocused(true)}
                  onBlur={blurProjBar}
                  placeholder="Search PO #, Project Name, or Custom Address…"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-indigo-500"
                />
                {filteredProjects.length > 0 && (
                  <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden max-h-40 overflow-y-auto bg-gray-800">
                    {filteredProjects.map(pr => (
                      <button
                        key={pr._id}
                        onClick={async () => {
                          const label = `${pr.title || 'Project'} (PO ${pr.poNumber})`;
                          setNewTask(t => ({
                            ...t, project: label, address: pr.address || '', projectRef: pr._id,
                            customerName: pr.customerName || t.customerName,
                            customerPhone: pr.customerPhone ? fmtPhoneParen(pr.customerPhone) : t.customerPhone,
                            // the project already knows who sent the job
                            workForName: pr.workForName || t.workForName,
                            workForPhone: pr.workForPhone || t.workForPhone,
                            workForRel: pr.workForRel || t.workForRel,
                          }));
                          rememberAssigned(label, pr.address || '');
                          newTaskProjectRef.current = label;
                          setProjSearch(label);
                          if (pr.address) {
                            const pin = await geocodeToPin({ address: pr.address });
                            setNewTask(t => ({ ...t, ...pin }));
                          }
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                      >
                        <span className="font-bold text-indigo-300">#{pr.poNumber}</span>
                        {pr.title ? <span className="text-zinc-400"> · {pr.title}</span> : ''}
                      </button>
                    ))}
                  </div>
                )}
                {addrSuggestions.length > 0 && (
                  <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-gray-700/50">Addresses</div>
                    {addrSuggestions.map(s => (
                      <button
                        key={s.placeId || s.description}
                        onClick={async () => {
                          setNewTask(t => ({ ...t, project: s.description, address: s.description, projectRef: null }));
                          rememberAssigned(s.description, s.description);
                          newTaskProjectRef.current = s.description;
                          setProjSearch(s.description);
                          setAddrSuggestions([]);
                          const pin = await geocodeToPin({ placeId: s.placeId });
                          setNewTask(t => ({ ...t, ...pin }));
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                      >
                        📍 {s.description}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={async () => {
                    const text = projSearch.trim();
                    if (!text || barIsPicked(projSearch)) { alert('Type the address into the search bar first, then click this.'); return; }
                    setNewTask(t => ({ ...t, project: text, address: text, projectRef: null }));
                    rememberAssigned(text, text);
                    newTaskProjectRef.current = text;
                    setProjSearch(text);
                    setAddrSuggestions([]);
                    const pin = await geocodeToPin({ address: text });
                    setNewTask(t => ({ ...t, ...pin }));
                  }}
                  className="mt-1.5 text-xs text-indigo-400 hover:text-indigo-300 hover:underline underline-offset-2 block"
                >
                  Enter custom address
                </button>
                {projBarFocused && !projSearch.trim() && !newTask.project && recentAssigned.length > 0 && (
                  <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-gray-700/50">Recent</div>
                    {recentAssigned.map(r => (
                      <button
                        key={r.label}
                        onClick={() => { setNewTask(t => ({ ...t, project: r.label, address: r.address, projectRef: null })); newTaskProjectRef.current = r.label; setProjSearch(r.label); }}
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* PO picked → the project already answers "who sent it": show a
                  quiet line (or a small expand link) instead of the big field. */}
              {newTask.projectRef && !wfExpanded ? (
                newTask.workForName ? (
                  <p className="text-xs text-zinc-500">
                    For: <span className="text-zinc-300 font-semibold">{newTask.workForName}</span>
                    {newTask.workForPhone ? ` · ${newTask.workForPhone}` : ''}
                    <button onClick={() => setWfExpanded(true)} className="ml-2 text-indigo-400 hover:text-indigo-300 underline underline-offset-2">change</button>
                  </p>
                ) : (
                  <button onClick={() => setWfExpanded(true)} className="text-xs text-indigo-400 hover:text-indigo-300 hover:underline underline-offset-2 block">
                    + From a referral company?
                  </button>
                )
              ) : (
              <div>
                <p className="text-zinc-400 text-sm mb-1">Work for <span className="text-zinc-600">(sub-work — the company that sent the job)</span></p>
                <input
                  value={newTask.workForName}
                  onChange={(e) => setNewTask(t => ({ ...t, workForName: e.target.value, workForRel: null }))}
                  placeholder="e.g. James Electric — searches your Relationships"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-indigo-500"
                />
                {(() => {
                  const q = newTask.workForName.trim().toLowerCase();
                  if (!q || newTask.workForRel) return null;
                  const hits = relationships.filter(r => r.company.toLowerCase().includes(q)).slice(0, 5);
                  if (!hits.length) return null;
                  return (
                    <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
                      {hits.map(r => (
                        <button
                          key={r.id}
                          onClick={() => setNewTask(t => ({ ...t, workForName: r.company, workForPhone: r.phone, workForRel: r.id }))}
                          className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0 flex justify-between gap-2"
                        >
                          <span className="truncate">{r.company}</span>
                          {r.phone && <span className="text-xs text-zinc-500 shrink-0">{r.phone}</span>}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              )}
              <div>
                <p className="text-zinc-400 text-sm mb-1">Event Type <span className="text-zinc-600">(optional)</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(EVENT_TYPES).map(([key, et]) => {
                    const on = newTask.eventType === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setNewTask(x => ({ ...x, eventType: on ? '' : key }))}
                        className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ring-1 transition-colors ${
                          on ? et.chip : 'bg-gray-800/70 text-zinc-500 ring-gray-700 hover:text-zinc-300'
                        }`}
                      >
                        {et.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 max-md:grid-cols-1 gap-3">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Customer</p>
                  <input
                    value={newTask.customerName}
                    onChange={(e) => { setCustPicked(''); setNewTask(t => ({ ...t, customerName: e.target.value })); }}
                    placeholder="Search Customers — or type a name"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-indigo-500"
                  />
                  {(() => {
                    const q = newTask.customerName.trim().toLowerCase();
                    if (!q || q === custPicked) return null;
                    const hits = allCustomers.filter(c => c.name.toLowerCase().includes(q)).slice(0, 5);
                    if (!hits.length) return null;
                    return (
                      <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
                        {hits.map(c => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setCustPicked(c.name.toLowerCase());
                              setNewTask(t => ({ ...t, customerName: c.name, customerPhone: c.phone ? fmtPhoneParen(c.phone) : t.customerPhone }));
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0 flex justify-between gap-2"
                          >
                            <span className="truncate">{c.name}</span>
                            {c.phone && <span className="text-xs text-zinc-500 shrink-0">{c.phone}</span>}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Customer Phone</p>
                  <input
                    type="tel"
                    value={newTask.customerPhone}
                    onChange={(e) => setNewTask(t => ({ ...t, customerPhone: fmtPhoneParen(e.target.value) }))}
                    placeholder="Optional"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Date</p>
                  <input
                    type="date"
                    value={newTask.date}
                    onChange={(e) => setNewTask(t => ({ ...t, date: e.target.value }))}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm mb-1 flex items-center gap-2">
                    Start Time
                    <input
                      type="checkbox"
                      checked={newTask.hasStart}
                      onChange={(e) => setNewTask(t => ({ ...t, hasStart: e.target.checked }))}
                      className="accent-indigo-500"
                    />
                  </p>
                  {newTask.hasStart ? (
                    <input
                      type="time"
                      value={newTask.startTime}
                      onChange={(e) => setNewTask(t => ({ ...t, startTime: e.target.value }))}
                      className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-indigo-500"
                    />
                  ) : (
                    <p className="px-3 py-2 text-zinc-500 font-medium">TBD</p>
                  )}
                </div>
                <div>
                  <p className="text-zinc-400 text-sm mb-1 flex items-center gap-2">
                    End Time
                    <input
                      type="checkbox"
                      checked={newTask.hasEnd}
                      onChange={(e) => setNewTask(t => ({
                        ...t, hasEnd: e.target.checked,
                        endTime: e.target.checked ? (t.endTime || plusOneHour(t.hasStart ? t.startTime : '')) : t.endTime,
                      }))}
                      className="accent-indigo-500"
                    />
                  </p>
                  {newTask.hasEnd && (
                    <input
                      type="time"
                      value={newTask.endTime}
                      onChange={(e) => setNewTask(t => ({ ...t, endTime: e.target.value }))}
                      className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-indigo-500"
                    />
                  )}
                </div>
              </div>
              <div>
                <p className="text-zinc-400 text-sm mb-1">Notes</p>
                <textarea
                  value={newTask.notes}
                  onChange={(e) => setNewTask(t => ({ ...t, notes: e.target.value }))}
                  placeholder="e.g. Rough-in, Trim, Service…"
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-indigo-500 resize-y"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={closeNewTask} className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium text-sm">Cancel</button>
              <button onClick={saveNewTask} className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium text-sm text-white">Assign</button>
            </div>
          </div>
        </div>
      )}

      {/* Details Popover — closing by ANY route (backdrop, ✕, Close) discards
          an unsaved edit draft so the card always reopens in its true state. */}
      {selectedAssignment && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeDetails}>
          <div
            className="bg-gray-900 rounded-2xl p-8 max-md:p-5 max-w-lg w-full border border-gray-700 shadow-2xl relative max-h-[90dvh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-zinc-400 hover:text-white text-xl"
              onClick={closeDetails}
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold mb-2 flex items-center gap-2.5 flex-wrap">Assignment Details {!editDraft && <EventBadge type={selectedAssignment.eventType} />}</h2>
            {editDraft && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {Object.entries(EVENT_TYPES).map(([key, et]) => {
                  const on = editDraft.eventType === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setEditDraft(d => ({ ...d, eventType: on ? '' : key }))}
                      className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ring-1 transition-colors ${on ? et.chip : 'bg-gray-800/70 text-zinc-500 ring-gray-700 hover:text-zinc-300'}`}
                    >
                      {et.label}
                    </button>
                  );
                })}
              </div>
            )}
            {!editDraft && <div className="mb-4" />}

            <div className="space-y-5">
              <div>
                <p className="text-zinc-400 text-sm">Employee</p>
                <p className="text-xl font-semibold text-zinc-200"><CopyText text={selectedAssignment.employeeName} field="emp" /></p>
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Project</p>
                {editDraft ? (
                  <div>
                    <input
                      autoFocus
                      value={projSearch}
                      onChange={(e) => setProjSearch(e.target.value)}
                      onFocus={() => setProjBarFocused(true)}
                      onBlur={blurProjBar}
                      placeholder="Search PO #, Project Name, or Custom Address…"
                      className="w-full bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 text-sm text-white outline-none"
                    />
                    {projBarFocused && !projSearch.trim() && recentAssigned.length > 0 && (
                      <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-gray-700/50">Recent</div>
                        {recentAssigned.map(r => (
                          <button
                            key={r.label}
                            onClick={() => { setEditDraft(d => ({ ...d, project: r.label, address: r.address })); rememberAssigned(r.label, r.address); setProjSearch(r.label); }}
                            className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {filteredProjects.length > 0 && (
                      <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden max-h-44 overflow-y-auto bg-gray-800">
                        {filteredProjects.map(p => (
                          <button
                            key={p._id}
                            onClick={async () => {
                              const label = `${p.title || 'Project'} (PO ${p.poNumber})`;
                              setEditDraft(d => ({ ...d, project: label, address: p.address || '', projectRef: p._id, customerName: p.customerName || d.customerName }));
                              rememberAssigned(label, p.address || '');
                              setProjSearch(label);
                              if (p.address) {
                                const pin = await geocodeToPin({ address: p.address });
                                setEditDraft(d => d ? { ...d, ...pin } : d);
                              }
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                          >
                            <span className="font-bold text-indigo-300">#{p.poNumber}</span>
                            {p.title ? <span className="text-zinc-400"> · {p.title}</span> : ''}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Google Places matches — custom addresses */}
                    {addrSuggestions.length > 0 && (
                      <div className="mt-1 border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-gray-700/50">Addresses</div>
                        {addrSuggestions.map(s => (
                          <button
                            key={s.placeId || s.description}
                            onClick={async () => {
                              setEditDraft(d => ({ ...d, project: s.description, address: s.description, projectRef: null }));
                              rememberAssigned(s.description, s.description);
                              setProjSearch(s.description);
                              setAddrSuggestions([]);
                              const pin = await geocodeToPin({ placeId: s.placeId });
                              setEditDraft(d => d ? { ...d, ...pin } : d);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                          >
                            📍 {s.description}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={async () => {
                        const text = projSearch.trim();
                        if (!text || barIsPicked(projSearch)) { alert('Type the address into the search bar first, then click this.'); return; }
                        setEditDraft(d => ({ ...d, project: text, address: text, projectRef: null }));
                        rememberAssigned(text, text);
                        setProjSearch(text);
                        setAddrSuggestions([]);
                        const pin = await geocodeToPin({ address: text });
                        setEditDraft(d => d ? { ...d, ...pin } : d);
                      }}
                      className="mt-1.5 text-xs text-indigo-400 hover:text-indigo-300 hover:underline underline-offset-2 block"
                    >
                      Enter custom address
                    </button>
                  </div>
                ) : (
                  <p className="text-lg font-medium">
                    {/* Custom-address task: the "project" IS the address — show
                        it once (below, under Address) and N/A here. */}
                    {selectedAssignment.address && selectedAssignment.address === selectedAssignment.project
                      ? <span className="text-zinc-500">N/A</span>
                      : <CopyText text={selectedAssignment.project} field="proj" />}
                  </p>
                )}
              </div>
              {/* Jobsite address — click/tap copies it; "Copied!" tooltip
                  floats above, same pattern as CustomerProfile. */}
              {(editDraft ? editDraft.address : selectedAssignment.address) && (
                <div>
                  <p className="text-zinc-400 text-sm">Address</p>
                  <p className="text-zinc-300">
                    <CopyText text={editDraft ? editDraft.address : selectedAssignment.address} field="addr" />
                  </p>
                </div>
              )}
              <div>
                <p className="text-zinc-400 text-sm">Customer</p>
                {editDraft ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={editDraft.customerName || ''}
                      onChange={(e) => setEditDraft(p => ({ ...p, customerName: e.target.value }))}
                      placeholder="Customer name"
                      className="bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 text-sm text-zinc-200 outline-none"
                    />
                    <input
                      value={editDraft.customerPhone || ''}
                      onChange={(e) => setEditDraft(p => ({ ...p, customerPhone: fmtPhoneParen(e.target.value) }))}
                      placeholder="Phone"
                      className="bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 text-sm text-zinc-200 outline-none"
                    />
                  </div>
                ) : (
                  <p className="text-zinc-300">
                    <CopyText text={selectedAssignment.customerName} field="cust" />
                    {selectedAssignment.customerPhone && (
                      <span className="text-zinc-400 text-sm"> · <CopyText text={selectedAssignment.customerPhone} field="custPh" /></span>
                    )}
                  </p>
                )}
              </div>
              {(selectedAssignment.workForName || (editDraft && editDraft.workForName !== undefined)) && (
                <div>
                  <p className="text-zinc-400 text-sm">Work for</p>
                  {editDraft ? (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        value={editDraft.workForName || ''}
                        onChange={(e) => setEditDraft(p => ({ ...p, workForName: e.target.value }))}
                        placeholder="Company"
                        className="bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 text-sm text-zinc-200 outline-none"
                      />
                      <input
                        value={editDraft.workForPhone || ''}
                        onChange={(e) => setEditDraft(p => ({ ...p, workForPhone: fmtPhoneParen(e.target.value) }))}
                        placeholder="Phone"
                        className="bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 text-sm text-zinc-200 outline-none"
                      />
                    </div>
                  ) : (
                    <p className="text-zinc-300">
                      <CopyText text={selectedAssignment.workForName} field="wfName" />
                      {selectedAssignment.workForPhone && (
                        <span className="text-zinc-400 text-sm"> · <CopyText text={selectedAssignment.workForPhone} field="wfPh" /></span>
                      )}
                    </p>
                  )}
                </div>
              )}
              <div>
                <p className="text-zinc-400 text-sm">Notes</p>
                {editDraft ? (
                  <textarea
                    value={editDraft.notes}
                    onChange={(e) => setEditDraft(p => ({ ...p, notes: e.target.value }))}
                    className="w-full bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 text-zinc-200 outline-none h-20 resize-none"
                  />
                ) : (
                  <p className="text-zinc-300 whitespace-pre-wrap break-words">{selectedAssignment.notes || 'No additional notes'}</p>
                )}
              </div>
              <div>
                {editDraft ? (
                  <div>
                    <p className="text-zinc-400 text-sm mb-1">Date</p>
                    <input
                      type="date"
                      value={editDraft.date}
                      onChange={(e) => setEditDraft(p => ({ ...p, date: e.target.value }))}
                      className="bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 font-medium text-white outline-none"
                    />
                    <div className="flex flex-wrap gap-4 mt-3">
                      <div>
                        <p className="text-zinc-400 text-sm mb-1 flex items-center gap-2">
                          Start Time
                          <input
                            type="checkbox"
                            checked={!!editDraft.hasStart}
                            onChange={(e) => setEditDraft(p => ({ ...p, hasStart: e.target.checked }))}
                            className="accent-indigo-500"
                          />
                        </p>
                        {editDraft.hasStart ? (
                          <input
                            type="time"
                            value={editDraft.startTime || ''}
                            onChange={(e) => setEditDraft(p => ({ ...p, startTime: e.target.value }))}
                            className="bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 font-medium text-white outline-none"
                          />
                        ) : (
                          <p className="px-3 py-2 text-zinc-500 font-medium">TBD</p>
                        )}
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm mb-1 flex items-center gap-2">
                          End Time
                          <input
                            type="checkbox"
                            checked={!!editDraft.hasEnd}
                            onChange={(e) => setEditDraft(p => ({
                              ...p, hasEnd: e.target.checked,
                              endTime: e.target.checked ? (p.endTime || plusOneHour(p.hasStart ? p.startTime : '')) : p.endTime,
                            }))}
                            className="accent-indigo-500"
                          />
                        </p>
                        {editDraft.hasEnd && (
                          <input
                            type="time"
                            value={editDraft.endTime || ''}
                            onChange={(e) => setEditDraft(p => ({ ...p, endTime: e.target.value }))}
                            className="bg-gray-800 border border-indigo-500/60 rounded-lg px-3 py-2 font-medium text-white outline-none"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-zinc-400 text-sm">Date</p>
                    <p className="text-lg font-medium">
                      <CopyText
                        field="date"
                        text={`${format(new Date(selectedAssignment.date), 'EEEE, MMMM d, yyyy')} @ ${selectedAssignment.startTime ? fmtTime(selectedAssignment.startTime) : 'TBD'}${selectedAssignment.endTime ? ` – ${fmtTime(selectedAssignment.endTime)}` : ''}`}
                      />
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Row 1: notify icon (+ Map pill on mobile) — hidden while editing */}
            {!editDraft && (
            <div className="mt-6 flex items-center gap-2">
              {(() => {
                const emp = employees.find(e => e.id === selectedAssignment.employeeId);
                const href = emp ? smsHref(emp.phone, notifBody(selectedAssignment)) : null;
                return href ? (
                  <a
                    href={href}
                    title="Notify Employee"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-indigo-600/20 border border-indigo-500/50 text-indigo-300 hover:bg-indigo-600/30 transition-colors"
                  >
                    <Send size={15} />
                  </a>
                ) : (
                  <button
                    onClick={() => alert(`No phone on file for ${selectedAssignment.employeeName || 'this employee'} — add one in their profile.`)}
                    title="Notify Employee (no phone on file)"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 border border-gray-700 text-zinc-500"
                  >
                    <Send size={15} />
                  </button>
                );
              })()}
              {/* Mobile: jump to this assignment's day on the fullscreen map.
                  The details card stays open underneath — closing the map
                  lands you right back on it. */}
              <button
                onClick={() => {
                  const d = new Date(selectedAssignment.date);
                  for (let i = 0; i < 7; i++) {
                    if (isSameDay(addDays(currentWeekStart, i), d)) { setSelectedDayIdx(i); break; }
                  }
                  setShowMobileMap(true);
                }}
                className="md:hidden px-3 py-1.5 rounded-full bg-gray-800 border border-gray-700 text-zinc-300 text-xs font-semibold"
              >
                Map
              </button>
            </div>
            )}

            {/* Row 2: Delete on the left, Edit/Save + Close on the right */}
            <div className="mt-3 flex flex-wrap justify-between items-center gap-2">
              <button
                onClick={() => deleteAssignment(selectedAssignment.id)}
                className="px-3 py-1.5 bg-red-900/60 hover:bg-red-800/80 text-red-200 rounded-lg font-medium text-xs transition-colors"
              >
                Delete
              </button>
              <div className="flex gap-2">
                {editDraft ? (
                  <button
                    onClick={saveEditDraft}
                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium text-xs text-white"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setEditDraft({
                        project: selectedAssignment.project || '',
                        notes: selectedAssignment.notes || '',
                        date: format(new Date(selectedAssignment.date), 'yyyy-MM-dd'),
                        hasStart: !!selectedAssignment.startTime,
                        startTime: selectedAssignment.startTime || '08:00',
                        hasEnd: !!selectedAssignment.endTime,
                        endTime: selectedAssignment.endTime || '',
                        customerName: selectedAssignment.customerName || '',
                        customerPhone: selectedAssignment.customerPhone || '',
                        workForName: selectedAssignment.workForName || '',
                        workForPhone: selectedAssignment.workForPhone || '',
                        eventType: selectedAssignment.eventType || '',
                      });
                      setProjSearch(selectedAssignment.project || '');
                    }}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium text-xs text-white"
                  >
                    Edit
                  </button>
                )}
                <button
                  className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium text-xs"
                  onClick={closeDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructions (desktop interactions — meaningless on touch) */}
      <div className="text-sm text-zinc-500 mt-8 max-md:hidden">
        Tip: Quick tap on a task block to see details. Click and drag immediately to move.
        Hold Command (⌘) while dragging to duplicate instead of moving.
        Hover over any task block and press Delete to remove it.
      </div>
    </div>
  );
}

export default EmployeeScheduling;